import React from "react";
import {
  Badge,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FaEllipsisH, FaSyncAlt } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { EmployerJobAd } from "./types";

export interface EnhancedEmployerJobAdsTableProps {
  data?: EmployerJobAd[];
  isLoading?: boolean;
  pageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string;
    endCursor: string;
  };
  onLoadMore?: () => void;
  onRefresh?: () => void;
  showPagination?: boolean;
}

export const EnhancedEmployerJobAdsTable: React.FC<EnhancedEmployerJobAdsTableProps> = ({
  data,
  isLoading,
  pageInfo,
  onLoadMore,
  onRefresh,
  showPagination = false,
}) => {
  const history = useHistory();

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "IN_REVIEW":
        return "orange";
      case "ACTIVE":
        return "green";
      case "STOPPED":
        return "orange";
      case "DISABLED":
        return "gray";
      case "REJECTED":
        return "red";
      default:
        return "gray";
    }
  };

  const getEmploymentTypeLabel = (type: string) => {
    return type === "FULL_TIME" ? "Full Time" : "Contract";
  };

  return (
    <div>
      <TableContainer>
        <Table className="border">
          <Thead>
            <Tr>
              <Th className="border">Date Created</Th>
              <Th className="border">Job Title</Th>
              <Th className="border">Organization</Th>
              <Th className="border">Employment Type</Th>
              <Th className="border">Location</Th>
              <Th className="border">Status</Th>
              <Th className="border">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr>
                <Td colSpan={7} className="border">
                  <div className="w-full flex justify-center items-center min-h-[80px]">
                    <p>
                      Loading <FaSyncAlt />
                    </p>
                  </div>
                </Td>
              </Tr>
            )}
            {!isLoading && !data?.length && (
              <Tr>
                <Td colSpan={7} className="border">
                  <div className="w-full flex justify-center items-center min-h-[80px]">
                    <p>No job ads found</p>
                  </div>
                </Td>
              </Tr>
            )}
            {data?.map((jobAd) => (
              <Tr key={jobAd.id}>
                <Td className="border">
                  {new Date(jobAd.createdAt).toLocaleDateString()}
                </Td>
                <Td className="border">
                  <Text fontWeight="medium">{jobAd.title}</Text>
                </Td>
                <Td className="border">{jobAd.organization.name}</Td>
                <Td className="border">
                  <Badge variant="outline">
                    {getEmploymentTypeLabel(jobAd.employmentType)}
                  </Badge>
                </Td>
                <Td className="border">
                  {jobAd.location.area}, {jobAd.location.state}
                </Td>
                <Td className="border">
                  <Badge colorScheme={getStatusBadgeColor(jobAd.status)}>
                    {jobAd.status.replace("_", " ")}
                  </Badge>
                </Td>
                <Td className="border">
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<FaEllipsisH />}
                      variant="outline"
                      size="xs"
                    />
                    <MenuList>
                      <MenuItem
                        onClick={() => history.push(`/job-ads/${jobAd.id}`)}
                      >
                        View Details
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {showPagination && pageInfo && (
        <Flex justify="space-between" align="center" mt={4}>
          <Text fontSize="sm" color="gray.600">
            Showing {data?.length || 0} job ads
          </Text>
          <HStack spacing={2}>
            {pageInfo.hasNextPage && (
              <Button
                size="sm"
                onClick={onLoadMore}
                isLoading={isLoading}
                loadingText="Loading"
              >
                Load More
              </Button>
            )}
            {onRefresh && (
              <Button
                size="sm"
                variant="outline"
                onClick={onRefresh}
                leftIcon={<FaSyncAlt />}
              >
                Refresh
              </Button>
            )}
          </HStack>
        </Flex>
      )}
    </div>
  );
};
